import React from 'react';
import { Link } from 'react-router-dom';
import { useStripe } from '../contexts/StripeContext';
import { useAuth } from '../contexts/AuthContext';

const plans = [
  {
    name: 'Pay as you go',
    credits: 1,
    price: 3.00,
    priceId: 'price_1RHBxIIDGwneZr2hnnOczpeI',
    highlight: false,
    badge: null,
    features: [
      '1 PDF summary',
      'Unlimited page length',
      'Chat with document',
      'Summary history',
    ],
  },
  {
    name: '10 Pack',
    credits: 10,
    price: 25.49,
    priceId: 'price_1RHC1SIDGwneZr2hgxmkKX6P',
    highlight: true,
    badge: 'Best value',
    features: [
      '10 PDF summaries',
      'Unlimited page length',
      'Chat with document',
      'Summary history',
      'Save $4.51 vs pay-as-you-go',
    ],
  },
];

const Check = () => (
  <svg className="w-4 h-4 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd"
      d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
      clipRule="evenodd"/>
  </svg>
);

export default function Pricing() {
  const { createCheckoutSession, loading } = useStripe();
  const { currentUser } = useAuth();

  return (
    <div>
      {/* Heading */}
      <div className="text-center mb-12">
        <p className="text-sm font-semibold tracking-widest uppercase mb-3"
          style={{ color: 'var(--accent-primary)' }}>
          Simple pricing
        </p>
        <h2 className="text-4xl font-extrabold theme-text-primary mb-4">
          Choose your plan
        </h2>
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border"
          style={{ borderColor: 'var(--accent-primary)', color: 'var(--accent-primary)', background: 'var(--accent-light)' }}>
          🎁 New users receive <strong>3 free credits</strong> on sign-up
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
        {plans.map((plan) => (
          <div
            key={plan.name}
            className="relative flex flex-col rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1"
            style={{
              background: plan.highlight
                ? 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)'
                : 'var(--bg-secondary)',
              border: plan.highlight ? 'none' : '1px solid var(--border-color)',
              boxShadow: plan.highlight
                ? '0 8px 40px rgba(79,70,229,0.45)'
                : 'var(--shadow-card)',
              color: plan.highlight ? '#fff' : 'inherit',
            }}
          >
            {plan.badge && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-400 text-amber-900">
                  {plan.badge}
                </span>
              </div>
            )}

            <h3 className="text-lg font-bold mb-1" style={{ color: plan.highlight ? '#fff' : 'var(--text-primary)' }}>
              {plan.name}
            </h3>
            <p className="text-sm mb-6" style={{ color: plan.highlight ? 'rgba(255,255,255,0.7)' : 'var(--text-secondary)' }}>
              {plan.credits} credit{plan.credits > 1 ? 's' : ''}
            </p>

            <div className="flex items-baseline gap-1 mb-8">
              <span className="text-5xl font-extrabold" style={{ color: plan.highlight ? '#fff' : 'var(--text-primary)' }}>
                ${plan.price}
              </span>
              <span className="text-sm" style={{ color: plan.highlight ? 'rgba(255,255,255,0.6)' : 'var(--text-secondary)' }}>
                one-time
              </span>
            </div>

            <ul className="space-y-3 mb-8 flex-1">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm"
                  style={{ color: plan.highlight ? 'rgba(255,255,255,0.85)' : 'var(--text-secondary)' }}>
                  <span style={{ color: plan.highlight ? '#a5b4fc' : 'var(--accent-primary)' }}>
                    <Check />
                  </span>
                  {f}
                </li>
              ))}
            </ul>

            {currentUser ? (
              <button
                onClick={() => createCheckoutSession(plan.priceId)}
                disabled={loading}
                className="w-full py-3 rounded-xl text-sm font-bold transition-all duration-200 disabled:opacity-50 hover:-translate-y-0.5"
                style={plan.highlight
                  ? { background: '#fff', color: '#4f46e5' }
                  : { background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: '#fff', boxShadow: '0 4px 14px rgba(79,70,229,0.3)' }
                }
              >
                {loading ? 'Processing…' : 'Get started'}
              </button>
            ) : (
              <Link
                to="/home"
                className="w-full py-3 rounded-xl text-sm font-bold text-center block transition-all duration-200 hover:-translate-y-0.5"
                style={plan.highlight
                  ? { background: '#fff', color: '#4f46e5' }
                  : { background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: '#fff' }
                }
              >
                Sign in to purchase
              </Link>
            )}
          </div>
        ))}
      </div>

      <p className="text-center mt-8 text-sm theme-text-secondary">
        All plans include access to the full AI summarization engine and document chat.
        <br />Credits never expire.
      </p>
    </div>
  );
}
